import React, { useState, useRef } from "react"
import { useParams } from "react-router-dom"
import * as Yup from "yup"
import { useQuery, useMutation } from "react-query"
import { Button } from "../../components/button";
import { Formik } from "formik"
import { Container, Grid } from '@material-ui/core';
import { fetchExperts, fetchCustomers, fetchCurrenceis, fetchVideoTypes, fetchPlatforms, fetchNiches, fetchProductDelivering, fetchToneOfVoices, fetchCountries, fetchLanguages, fetchContentTypes, fetchVideoFormats, fetchProjects, fetchProjectVideos, fetchProjectStatus, updateProject, addVideoAssigne, removeVideoAssigne, deleteVideoFile, deleteVideoScript, fetchFileOriginalUrl } from "../../services"
import Select from "react-select"
import BriefRecommendations from "../../components/BriefRecommendations";
import { PayButton } from "../../components/PayButton";
import { Editor } from '@tinymce/tinymce-react'
import { FillVideoDetails } from "../../components/FillVideoDetails";
import VideoRecommendationsModal from "../../components/VideoRecommendationsModal";
import { ViewVideoDetails } from "../../components/ViewVideoDetails";
import { Divider, Tooltip } from "@mui/material";
import { ViewLeaveHistoryModal } from "../../components/ViewLeaveHistory";
import { ProductionQuantityLimitsOutlined } from "@mui/icons-material";
import VideoReimburseModal from "../../components/VideoReimburseModal";
import { fetchDeals, linkCampaign, unlinkCampaign } from "../../services/deal";

function EditUGCProject() {
  const { projectId } = useParams()
  const formikRef = useRef(null)
  const [videoDetailsOpen, setVideoDetailsOpen] = useState(null)
  const [viewVideoDetailsOpen, setViewVideoDetailsOpen] = useState(null)
  const [viewLeaveHistoryOpen, setViewLeaveHistoryOpen] = useState(null)
  const [videoRecoOpen, setVideoRecoOpen] = useState(null)
  const [videoReimburseOpen, setVideoReimburseOpen] = useState(null)
  const [expertOptions, setExpertOptions] = useState([])
  const [typeOptions, setTypeOptions] = useState([])
  const [country, setCountry] = useState()
  const [currency, setCurrency] = useState(3)
  const [isInfluencer, setIsInfluencer] = useState(false)
  const genders = [
    { value: 'm', label: 'Male' },
    { value: 'f', label: 'Female' },
  ]

  const ages = [
    { value: 'Less 18 years', label: 'Less 18 years' },
    { value: '18-30 years', label: '18-30 years' },
    { value: 'More than 30 years', label: 'More than 30' },
  ]

  const [editMode, setEditMode] = useState(null)

  const places = [
    { value: 'in', label: 'Indoor' },
    { value: 'out', label: 'Outdoor' },
  ]

  const durations = [
    { value: 'Up to 15 sec', label: 'Up to 15 sec' },
    { value: '15 - 30 Sec', label: '15 - 30 Sec' },
    { value: '30 - 60 Sec', label: '30 - 60 Sec' },
    { value: 'Other', label: 'Other' },
  ]

  const emptyVideoObject = {
    expert_id: null,
    type_id: null,
  }

  const { data: project, isLoading, refetch, isFetching } = useQuery("project", () => fetchProjects(projectId), {
    refetchOnWindowFocus: false,
    select: (data) => data.projects[0],
    onSuccess: project => {
      setCurrency(project?.currency_id)
      const countryId = project?.brief?.country?.value
      if ([1, 2].includes(countryId)) {
        setCountry(countryId)
      }
      else {
        setCountry('')
      }
      setIsInfluencer(project?.is_influencer)
    }
  })

  const { data: videos = [], isLoading: isVideosLoading, refetch: refetchVideos } = useQuery("videos", () => fetchProjectVideos(projectId, { show_cancelled: 1, }), {
    refetchOnWindowFocus: true,
    select: (data) => data.videos,
  })

  const { data: currencyOptions = [], isLoading: isCurrenciesLoading } = useQuery(['currencies'], () => fetchCurrenceis(), {
    select: res => res.currencies?.map(e => ({ label: `${e.name} - ${e.code}`, value: e.id })),
    refetchOnWindowFocus: false,
  })

  const paymentMethods = [
    {
      label: 'Stripe',
      value: process.env.REACT_APP_PAYMENT_METHOD_STRIPE,
    },
    {
      label: 'Bank Transfer',
      value: process.env.REACT_APP_PAYMENT_METHOD_BANK_TRANSFER,
    },
  ]

  const { data: experts = [], isLoading: isExpertsLoading } = useQuery(["experts"], () => fetchExperts(165, 3), {
    select: (data) => data.experts,
    onSuccess: experts => {
      setExpertOptions(experts.map(e => ({ label: `${e.name} - ${e.email} ${e.avg_rating ? `- AvgRating: ${Math.round(e.avg_rating)}` : ''}`, value: e.expert_id })))
    },
    refetchOnWindowFocus: false,
  })

  const { isLoading: isCustomersLoading, data: customers } = useQuery("customers", fetchCustomers, {
    select: (data) => data.customers.map(c => {
      return { label: `${c.name} - ${c.email}`, value: c.id }
    }),
    refetchOnWindowFocus: false,
  })

  const { data: videoTypes = [], isLoading: isVideoTypesLoading } = useQuery(["video-types", currency, country, isInfluencer], () => fetchVideoTypes({
    currency_id: currency,
    country_id: country,
    is_influencer: isInfluencer ? true : undefined,
    include_private: true,
  }), {
    select: (data) => data.plans,
    onSuccess: types => {
      setTypeOptions(types.map(e => ({ label: e.name, value: e.id })))
    },
    refetchOnWindowFocus: false,
  })

  const { data: projectStatusOptions, isLoading: projectStatusLoading } = useQuery("project_status", () => fetchProjectStatus(), {
    refetchOnWindowFocus: false,
    select: (data) => data.status.map(s => {
      return { label: s.name, value: s.id }
    })
  })

  const { data: platforms = [], isLoading: isPlatformsLoading } = useQuery(["platforms"], () => fetchPlatforms(), {
    select: (data) => data.platforms.map(e => ({ label: e.name, value: e.id, slug: e.slug })),
    refetchOnWindowFocus: false,
  })

  const { data: niches = [], isLoading: isNichesLoading } = useQuery(["niches"], () => fetchNiches(), {
    select: (data) => data.niches.map(e => ({ label: e.name, value: e.id })),
    refetchOnWindowFocus: false,
  })

  const { data: delivering = [], isLoading: isDeliveringLoading } = useQuery(["delivering"], () => fetchProductDelivering(), {
    select: (data) => data.proudct_delivering.map(e => ({ label: e.name, value: e.id })),
    refetchOnWindowFocus: false,
  })

  const { data: tones = [], isLoading: isTonesLoading } = useQuery(["tones"], () => fetchToneOfVoices(), {
    select: (data) => data.tones.map(e => ({ label: e.name, value: e.id, slug: e.slug })),
    refetchOnWindowFocus: false,
  })

  const { data: countries = [], isLoading: isCountriesLoading } = useQuery(["countries"], () => fetchCountries(), {
    select: (data) => data.countries.map(e => ({ label: e.name, value: e.id })),
    refetchOnWindowFocus: false,
  })

  const { data: languages = [], isLoading: isLanguagesLoading } = useQuery(["languages"], () => fetchLanguages(), {
    select: (data) => data.languages.map(e => ({ label: e.name, value: e.id, code: e.code })),
    refetchOnWindowFocus: false,
  })

  const { data: contentTypes = [], isLoading: isContentTypeLoading } = useQuery(["content-types"], () => fetchContentTypes(), {
    select: (data) => data.content_types.map(e => ({ label: e.name, value: e.id, slug: e.slug })),
    refetchOnWindowFocus: false,
  })

  const { data: formats = [], isLoading: isFormatsLoading } = useQuery(["video-formats"], () => fetchVideoFormats(), {
    select: (data) => data.video_formats.map(e => ({ label: e.name, value: e.id, slug: e.slug, ratio: e.ratio })),
    refetchOnWindowFocus: false,
  })

  const mutation = useMutation(updatedProject => updateProject(updatedProject, projectId), {
    onSuccess: () => {
      alert("Project successfully updated !")
      refetch()
    },
    onError: (error) => {
      alert(error.data?.message)
    }
  })

  const constcutBriefJSON = (data) => {
    const { platform, niche, product_delivery, country, language, tone, content_type, video_format, instructions, reference } = data
    const brief = {}

    const insertItem = (key, value, source) => {
      const item = source.find(e => e.value === value)
      if (item) {
        brief[key] = {
          value: item.value,
          label: item.label,
        }
      }
    }

    // insertItem('platform', platform, platforms)
    insertItem('niche', niche, niches)
    insertItem('product_delivering', product_delivery, delivering)
    insertItem('country', country, countries)
    // insertItem('language', language, languages)
    // insertItem('toneOfVoice', tone, tones)
    // insertItem('contentType', content_type, contentTypes)
    // insertItem('format', video_format, formats)

    brief.instructions = instructions
    brief.reference = reference

    return JSON.stringify(brief)
  }

  const deconstructBriefValues = (brief) => {
    const { platform, niche, product_delivering, country, language, toneOfVoice, contentType, format, instructions, reference } = brief
    const data = {
      // platform: platform?.value || null,
      niche: niche?.value || null,
      product_delivery: product_delivering?.value || null,
      country: country?.value || null,
      // language: language?.value || null,
      // tone: toneOfVoice?.value || null,
      // content_type: contentType?.value || null,
      // video_format: format?.value || null,
      // instructions: instructions || null,
      // reference: reference || null,
    }

    return data
  }

  const calculatePrice = (video, formatted = true) => {
    const type = videoTypes.find(e => e.id === video.type_id)
    if (type) {
      return formatted ? type.price_formatted : type.price_converted
    }
    return null
  }

  const calculatePayout = (video, index, videos = [], formatted = true) => {
    const type = videoTypes.find(e => e.id === video.type_id)
    if (type) {
      if (videos.some((e, i) => e.expert_id && e.expert_id === video.expert_id && i < index)) {
        return formatted ? type.softened_earning_formatted : type.softened_earning_converted
      }
      return formatted ? type.earning_formatted : type.earning_converted
    }
    return null
  }

  const { mutate: assignVideo, isAssigning } = useMutation(({ video_id, ...data }) => addVideoAssigne(video_id, data), {
    onSuccess: () => {
      alert("Creator assigned successfully")
      refetchVideos()
      setEditMode(null)
    },
    onError: error => {
      alert(error.data?.message)
    },
  })

  const { mutate: unassignVideo, isUnassigning } = useMutation(video_id => removeVideoAssigne(video_id), {
    onSuccess: () => {
      alert("Creator un-assigned successfully")
      refetchVideos()
    },
    onError: error => {
      alert(error.data?.message)
    },
  })

  const { mutate: deleteVideo, isDeleting } = useMutation(({ video_id, file_id }) => deleteVideoFile(video_id, file_id), {
    onSuccess: () => {
      alert("Video deleted successfully")
    },
    onError: error => {
      alert(error.data?.message)
    },
  })

  const { mutate: deleteScript, isDeleting: isDeletingScript } = useMutation(({ video_id, script_id }) => deleteVideoScript(video_id, script_id), {
    onSuccess: () => {
      alert("Video script deleted successfully")
    },
    onError: error => {
      alert(error.data?.message)
    },
  })

  // Deal assignment
  const [selectedDealId, setSelectedDealId] = useState(null)

  const { data: dealOptions = [], isLoading: isDealsLoading } = useQuery(
    "deals-for-project",
    () => fetchDeals({ limit: 200 }),
    {
      select: res => res.deals?.map(d => ({ value: d.id, label: `${d.name} (${d.country_name || ""})` })) || [],
      refetchOnWindowFocus: false,
    }
  )

  // Sync selectedDealId once project loads
  React.useEffect(() => {
    if (project && selectedDealId === null) {
      setSelectedDealId(project.deal_id || undefined)
    }
  }, [project])

  const { mutate: saveDeal, isLoading: isSavingDeal } = useMutation(
    async () => {
      const currentDealId = project?.deal_id
      if (selectedDealId === currentDealId) return
      if (selectedDealId) {
        return linkCampaign(selectedDealId, projectId)
      } else if (currentDealId) {
        return unlinkCampaign(currentDealId, projectId)
      }
    },
    {
      onSuccess: () => { alert("Deal assignment updated"); refetch() },
      onError: err => alert(err?.data?.message || "Failed to update deal assignment"),
    }
  )

  const handleOnAssign = (video_id, expert_id) => {
    assignVideo({ video_id, expert_id })
  }

  const handleOnUnassign = (video_id, video) => {
    if (video.is_approved) {
      alert("You can't un-assign creator, the video is already approved!")
    }
    else if (video.file) {
      if (window.confirm("Are you sure you want to procees? creator have video uploaded!")) {
        unassignVideo(video_id)
      }
    }
    else if (video.script) {
      if (window.confirm("Are you sure you want to procees? creator have script submitted!")) {
        unassignVideo(video_id)
      }
    }
    else {
      if (window.confirm("Are you sure you want to un-assign creator from this video?")) {
        unassignVideo(video_id)
      }
    }
  }

  const confirmDeleteVideoFile = (video_id, file_id) => {
    if (window.confirm("Are you sure you want to delete this video file?")) {
      deleteVideo({ video_id, file_id })
    }
  }

  const confirmDeleteScript = (video_id, script_id) => {
    if (window.confirm("Are you sure you want to delete this script?")) {
      deleteScript({ video_id, script_id })
    }
  }

  const handleOnVideoFormSubmit = () => {
    setVideoDetailsOpen(false)
    refetch()
  }

  const openReimburse = (video) => setVideoReimburseOpen(video)
  const closeReimburse = () => setVideoReimburseOpen(null)

  if (isLoading) {
    return (
      <div className="content-wrapper mb-55">
        <Container spacing={1}>
          <p>Loading..</p>
        </Container>
      </div>
    )
  }

  return (
    <div className="content-wrapper mb-55">
      <Container spacing={1}>
        <Grid item lg={12}>
          <div className="is-flex aic jcsb">
            <h2 className="mt-0 mb-5 mt-20">Edit UGC project</h2>
            {isLoading ? '' : <PayButton project={project} />}
          </div>
          {isLoading || !project ? "Loading.." : <Formik
            innerRef={formikRef}
            initialValues={{
              status_id: project.status_id,
              name: project.name || '',
              goal: project.goal || '',
              owner_id: project.owner_id,
              currency: project.currency_id,
              is_influencer: project.is_influencer,
              is_enterprise: project.is_enterprise,
              enterprise_fees: project.enterprise_fees || null,
              extra_cogs: project.extra_cogs || 0,
              payment_method: project.payment_method_id,
              account_manager_id: project.account_manager_id,
              ...deconstructBriefValues(project.brief),
            }}
            validationSchema={Yup.object().shape({
              status_id: Yup.number().required("Please select a valid project status"),
              name: Yup.string().required("Please enter title for this project"),
              goal: Yup.string().required("Please enter description for this project"),
              owner_id: Yup.number().required("Please select an owner for this project"),
              payment_method: Yup.string().required("Please specify a payment method for this project"),
              currency: Yup.string().required("Please specify a currency for this project"),
              enterprise_fees: Yup.number().nullable().when('is_enterprise', {
                is: true,
                then: Yup.number().min(1).required("Please set the enterprise fees")
              }),
            })}
            onSubmit={(values, { setSubmitting, errors }) => {
              const data = {
                status_id: values.status_id,
                name: values.name,
                goal: values.goal,
                owner_id: values.owner_id,
                account_manager_id: values.account_manager_id,
                is_enterprise: values.is_enterprise,
                enterprise_fees: values.enterprise_fees,
                extra_cogs: values.extra_cogs,
              }

              // if (values.status_id === Number(process.env.REACT_APP_PROJECT_STATUS_ONGOING) && project.status_id !== Number(process.env.REACT_APP_PROJECT_STATUS_ONGOING)) {
              if (project.status_id === Number(process.env.REACT_APP_PROJECT_STATUS_AWAITING_PAYMENT)) {
                if (values.status_id !== Number(process.env.REACT_APP_PROJECT_STATUS_AWAITING_PAYMENT)) {
                  data.newProjectServices = videos.filter(e => e.expert_id).reduce((a, c) => {
                    const expert = a.find(e => e.expert_id === c.expert_id)
                    if (!expert) {
                      a.push({
                        expert_id: c.expert_id,
                        service_id: project.service_id,
                        duration: 1,
                        cost: 0,
                        payout: 0,
                        hours: 0,
                        revisions: null,
                      })
                    }
                    return a
                  }, [])
                }
              } else if (values.status_id !== Number(process.env.REACT_APP_PROJECT_STATUS_AWAITING_PAYMENT)) {
                data.newProjectServices = videos.filter(e => e.expert_id).reduce((a, c) => {
                  const expert = a.find(e => e.expert_id === c.expert_id)
                  if (!expert) {
                    a.push({
                      expert_id: c.expert_id,
                      service_id: project.service_id,
                      duration: 1,
                      cost: 0,
                      payout: 0,
                      hours: 0,
                      revisions: null,
                    })
                  }
                  return a
                }, [])
              }

              data.brief = constcutBriefJSON(values)

              data.projectVideos = videos.map((v, i) => ({
                ...v,
                cost: calculatePrice(v, false),
                payout: calculatePayout(v, i, videos, false),
              }))
              data.price = data.projectVideos.reduce((a, c) => a + (c.cost || 0), 0) + Number(data.is_enterprise ? data.enterprise_fees : 0)

              mutation.mutate(data)
              setSubmitting(false)
            }}
          >
            {
              props => {
                const {
                  values,
                  touched,
                  setFieldValue,
                  errors,
                  isSubmitting,
                  handleChange,
                  handleBlur,
                  handleSubmit
                } = props

                return (
                  <form className="is-flex jcsb fdc" onSubmit={handleSubmit}>
                    <div>
                      <div className="is-flex aic mt-0 mb-10">
                        <h4 className="mt-15 mr-15 dimmed">PROJECT INFORMATION</h4>
                      </div>

                      {/* Project Status */}
                      <div className="group">
                        <Grid container spacing={2}>
                          <Grid item lg={6}>
                            <label htmlFor="name">Project status</label>
                            <Select
                              options={projectStatusOptions?.map(s => {
                                if (project.payment_method_id == process.env.REACT_APP_PAYMENT_METHOD_BANK_TRANSFER
                                  && s.value == process.env.REACT_APP_PROJECT_STATUS_FINISHED) {
                                  s.isDisabled = true
                                }
                                return s
                              })}
                              isLoading={projectStatusLoading}
                              name="status_id"
                              onChange={({ value }) => setFieldValue("status_id", value)}
                              value={projectStatusOptions?.find(s => {
                                return s.value === values.status_id
                              })}
                            />
                            {errors.status_id && touched.status_id ? (
                              <span className="v-error">{errors.status_id}</span>
                            ) : null}
                          </Grid>
                        </Grid>
                      </div>

                      {/* Deal Assignment */}
                      <div className="group">
                        <Grid container spacing={2}>
                          <Grid item lg={6}>
                            <label>Deal (Premier)</label>
                            <div className="is-flex aic" style={{ gap: 10 }}>
                              <div style={{ flex: 1 }}>
                                <Select
                                  isClearable
                                  isLoading={isDealsLoading}
                                  options={dealOptions}
                                  placeholder="No deal assigned"
                                  value={dealOptions.find(o => o.value === selectedDealId) || null}
                                  onChange={option => setSelectedDealId(option ? option.value : undefined)}
                                />
                              </div>
                              <button
                                type="button"
                                className="stllr-btn primary bordered btn-md"
                                disabled={isSavingDeal}
                                onClick={() => saveDeal()}
                              >
                                {isSavingDeal ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </Grid>
                        </Grid>
                      </div>

                      {/* Project Title */}
                      <div className="group">
                        <Grid container spacing={2}>
                          <Grid item lg={6}>
                            <label htmlFor="name">Project title</label>
                            <input type="text" name="name" value={values.name} onChange={handleChange} className="stllr-input" placeholder="Enter project title" />
                            {errors.name && touched.name ? (
                              <span className="v-error">{errors.name}</span>
                            ) : null}
                          </Grid>
                        </Grid>
                      </div>

                      {/* Project Description */}
                      <div className="group">
                        <Grid container spacing={2}>
                          <Grid item lg={6}>
                            <label htmlFor="goal">Project Description</label>
                            <Editor
                              apiKey={process.env.REACT_APP_TINY_API_KEY}
                              init={{
                                width: '100%',
                                height: 200,
                                menubar: false,
                                placeholder: 'Enter project description',
                                plugins: [
                                  'advlist autolink lists link image charmap print preview anchor',
                                  'searchreplace visualblocks code fullscreen',
                                  'insertdatetime media table paste code help wordcount', 'textcolor'
                                ],
                                toolbar: ' bullist numlist ',
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                              }}
                              onEditorChange={value => setFieldValue('goal', value)}
                              value={values.goal}
                            />
                            {/* <textarea
                              name="goal"
                              value={values.goal}
                              onChange={handleChange}
                              className="stllr-input"
                              placeholder="Enter project description"
                            ></textarea> */}
                            {errors.goal && touched.goal ? (
                              <span className="v-error">{errors.goal}</span>
                            ) : null}
                          </Grid>
                        </Grid>
                      </div>

                      {/* Project Project Owner */}
                      <div className="group">
                        <Grid container direction="row" spacing={2}>
                          <Grid item lg={3}>
                            <label htmlFor="owner_id">Project owner</label>
                            <Select
                              options={customers}
                              isLoading={isCustomersLoading}
                              value={customers && customers.find(c => c.value === values.owner_id)}
                              name="owner_id"
                              id="owner_id"
                              className="has-full-width"
                              onChange={(option) => setFieldValue("owner_id", option.value)}
                              // isDisabled
                              styles={{
                                control: base => ({
                                  ...base,
                                  height: 40,
                                  minHeight: 40
                                })
                              }}
                            />
                            {errors.owner_id && touched.owner_id ? (
                              <span className="v-error">{errors.owner_id}</span>
                            ) : null}
                          </Grid>
                        </Grid>
                      </div>

                      <div className="sep mb-18" />

                      <div className="group">
                        <Grid container direction="row" spacing={2}>
                          <Grid item lg={3}>
                            <label>
                              <input
                                type="checkbox"
                                name="is_enterprise"
                                disabled={project.status_id != process.env.REACT_APP_PROJECT_STATUS_AWAITING_PAYMENT || values.status_id != process.env.REACT_APP_PROJECT_STATUS_AWAITING_PAYMENT}
                                checked={values.is_enterprise}
                                onChange={e => setFieldValue('is_enterprise', e.target.checked)
                                }
                              />{' '}
                              Is this an Enterprise project?
                            </label>
                          </Grid>

                          <Grid item lg={4}>
                            <label htmlFor="name">Extra enterprise fees</label>
                            <input
                              type="number"
                              name="enterprise_fees"
                              value={values.enterprise_fees}
                              disabled={!values.is_enterprise || project.status_id != process.env.REACT_APP_PROJECT_STATUS_AWAITING_PAYMENT || values.status_id != process.env.REACT_APP_PROJECT_STATUS_AWAITING_PAYMENT}
                              onChange={e => setFieldValue('enterprise_fees', e.target.value)
                              }
                              className="stllr-input mb-10"
                              placeholder="Enter extra fees"
                            />
                            {errors.enterprise_fees && touched.enterprise_fees ? (
                              <span className="v-error">{errors.enterprise_fees}</span>
                            ) : null}
                          </Grid>

                          <Grid item lg={4}>
                            <label htmlFor="cogs">Extra COGS</label>
                            <input
                              type="number"
                              name="extra_cogs"
                              value={values.extra_cogs}
                              onChange={e => setFieldValue('extra_cogs', e.target.value)}
                              className="stllr-input mb-10"
                              placeholder="Enter extra COGS amount"
                            />
                          </Grid>
                        </Grid>
                      </div>

                      <div className="group">
                        <Grid container direction="row" spacing={2}>
                          <Grid item lg={3}>
                            <label>
                              <input
                                type="checkbox"
                                name="is_influencer"
                                checked={values.is_influencer}
                                disabled={true}
                                onChange={e => {
                                  setFieldValue('is_influencer', e.target.checked)
                                  setIsInfluencer(e.target.checked)
                                }}
                              />{' '}
                              Is this an Influencer project?
                            </label>
                          </Grid>
                        </Grid>
                      </div>

                      <div className="sep mb-18" />

                      {/* Project payment method/currency */}
                      <div className="group">
                        <Grid container direction="row" spacing={2}>
                          <Grid item lg={3}>
                            <label htmlFor="name">Payment Method</label>
                            <Select
                              name='payment_method'
                              options={paymentMethods}
                              value={paymentMethods.find(p => Number(p.value) === values.payment_method)}
                              placeholder="Select payment method"
                              // onChange={({ value }) => setFieldValue('payment_method', value)}
                              isDisabled
                              styles={{
                                control: base => ({
                                  ...base,
                                  height: 42,
                                  minHeight: 42
                                })
                              }}
                            />
                            {errors.payment_method && touched.payment_method ? (
                              <span className="v-error">{errors.payment_method}</span>
                            ) : null}
                          </Grid>

                          <Grid item lg={3}>
                            <label htmlFor="name">Project Currency</label>
                            <Select
                              name='currency'
                              options={currencyOptions}
                              value={currencyOptions?.find?.(e => e.value === values.currency)}
                              isLoading={isCurrenciesLoading}
                              isDisabled={true}
                              placeholder="Select currency"
                              // onChange={({ value }) => {
                              //   setFieldValue('currency', value)
                              //   setCurrentCurrency(currencies.find(e => e.id === value))
                              // }}
                              styles={{
                                control: base => ({
                                  ...base,
                                  height: 42,
                                  minHeight: 42
                                })
                              }}
                            />
                            {errors.currency ? (
                              <span className="v-error">{errors.currency}</span>
                            ) : null}
                          </Grid>
                        </Grid>
                      </div>

                      {/* Account Manager */}
                      <div className="group">
                        <Grid container direction="row" spacing={2}>
                          <Grid item lg={3}>
                            <label htmlFor="name">Account Manager</label>
                            <Select
                              options={customers}
                              value={customers && customers.find(c => c.value === values.account_manager_id)}
                              name="account_manager_id"
                              onChange={({ value }) => setFieldValue("account_manager_id", value)}
                            />
                            {errors.account_manager_id && touched.account_manager_id ? (
                              <span className="v-error">{errors.account_manager_id}</span>
                            ) : null}
                          </Grid>
                        </Grid>
                      </div>

                    </div>

                    <div className="sep mb-18" />
                    <div className="is-flex aic mt-0 mb-10">
                      <h4 className="mt-15 mr-15 dimmed">BRIEF DETAILS</h4>
                    </div>

                    <div className="group">
                      <Grid container direction="row" spacing={2}>
                        {/* <Grid item lg={3}>
                          <Select
                            name='platform'
                            value={platforms.find(e => e.value === values.platform)}
                            options={platforms}
                            placeholder="Select platform"
                            onChange={({ value }) => setFieldValue('platform', value)}
                            isDisabled={videos.some(video => video.expert_id !== null)}
                            styles={{
                              control: base => ({
                                ...base,
                                height: 42,
                                minHeight: 42
                              })
                            }}
                          />
                        </Grid> */}
                        <Grid item lg={3}>
                          <Select
                            name='niche'
                            value={niches.find(e => e.value === values.niche)}
                            options={niches}
                            placeholder="Select niche"
                            onChange={({ value }) => setFieldValue('niche', value)}
                            isDisabled={videos.some(video => video.expert_id !== null)}
                            styles={{
                              control: base => ({
                                ...base,
                                height: 42,
                                minHeight: 42
                              })
                            }}
                          />
                        </Grid>
                        <Grid item lg={3}>
                          <Select
                            name='product_delivery'
                            value={delivering.find(e => e.value === values.product_delivery)}
                            options={delivering}
                            placeholder="Select product delivery"
                            onChange={({ value }) => setFieldValue('product_delivery', value)}
                            isDisabled={videos.some(video => video.expert_id !== null)}
                            styles={{
                              control: base => ({
                                ...base,
                                height: 42,
                                minHeight: 42
                              })
                            }}
                          />
                        </Grid>
                        {/* <Grid item lg={3}>
                          <Select
                            name='tone'
                            value={tones.find(e => e.value === values.tone)}
                            options={tones}
                            placeholder="Select tone of voice"
                            onChange={({ value }) => setFieldValue('tone', value)}
                            isDisabled={videos.some(video => video.expert_id !== null)}
                            styles={{
                              control: base => ({
                                ...base,
                                height: 42,
                                minHeight: 42
                              })
                            }}
                          />
                        </Grid> */}
                        <Grid item lg={3}>
                          <Select
                            name='country'
                            value={countries.find(e => e.value === values.country)}
                            options={countries}
                            placeholder="Select country"
                            onChange={({ value }) => setFieldValue('country', value)}
                            // isDisabled={videos.some(video => video.expert_id !== null)}
                            isDisabled={true} // disable because every country has different video typs
                            styles={{
                              control: base => ({
                                ...base,
                                height: 42,
                                minHeight: 42
                              })
                            }}
                          />
                        </Grid>
                        {/* <Grid item lg={3}>
                          <Select
                            name='language'
                            value={languages.find(e => e.value === values.language)}
                            options={languages}
                            placeholder="Select language"
                            onChange={({ value }) => setFieldValue('language', value)}
                            isDisabled={videos.some(video => video.expert_id !== null)}
                            styles={{
                              control: base => ({
                                ...base,
                                height: 42,
                                minHeight: 42
                              })
                            }}
                          />
                        </Grid> */}
                        {/* <Grid item lg={3}>
                          <Select
                            name='content_type'
                            value={contentTypes.find(e => e.value === values.content_type)}
                            options={contentTypes}
                            placeholder="Select content type"
                            onChange={({ value }) => setFieldValue('content_type', value)}
                            isDisabled={videos.some(video => video.expert_id !== null)}
                            styles={{
                              control: base => ({
                                ...base,
                                height: 42,
                                minHeight: 42
                              })
                            }}
                          />
                        </Grid> */}
                        {/* <Grid item lg={3}>
                          <Select
                            name='video_format'
                            value={formats.find(e => e.value === values.video_format)}
                            options={formats}
                            placeholder="Select video format"
                            onChange={({ value }) => setFieldValue('video_format', value)}
                            isDisabled={videos.some(video => video.expert_id !== null)}
                            styles={{
                              control: base => ({
                                ...base,
                                height: 42,
                                minHeight: 42
                              })
                            }}
                          />
                        </Grid> */}
                        {/* <Grid item lg={12}>
                          <textarea
                            name="instructions"
                            value={values.instructions}
                            onChange={handleChange}
                            className="stllr-input"
                            placeholder="Enter instructions for creators"
                            disabled={videos.some(video => video.expert_id !== null)}
                          ></textarea>
                        </Grid>
                        <Grid item lg={12}>
                          <input
                            type="text"
                            name="reference"
                            value={values.reference}
                            onChange={handleChange}
                            className="stllr-input"
                            placeholder="Enter reference url"
                            disabled={videos.some(video => video.expert_id !== null)}
                          />
                        </Grid> */}
                      </Grid>
                    </div>

                    <input
                      type="submit"
                      className="stllr-btn primary btn-lg"
                      disabled={mutation.isLoading}
                      value="Update project"
                    />
                    {Object.keys(errors).length ? <span className="mt-10 v-error">Please check for wrong/missing fields [{Object.keys(errors).join(', ')}]</span> : ``}
                  </form>
                )
              }
            }
          </Formik>}
        </Grid>

        <div className="sep mb-18" />

        {project.is_offer && project.experts?.[0] ? (<div>
          <h4 className="mt-15 mr-15 dimmed">Offer sent to</h4>
          <div className="is-flex aic">
            <img src={project.experts[0].expert_avatar} alt={project.experts[0].expert_name} width={50} height={50} className="rounded-sm mr-10" />
            <div>
              <p className="fw-700">{project.experts[0].expert_name}</p>
              <p className="">{project.experts[0].expert_email}</p>
            </div>
          </div>
        </div>) : null}


        <div className="sep mb-18" />

        <div className="is-flex aic mt-0 mb-10">
          <h4 className="mt-15 mr-15 dimmed">Project Videos</h4>
        </div>
        {project?.status_id === Number(process.env.REACT_APP_PROJECT_STATUS_AWAITING_PAYMENT) ? <p className="mb-30">
          Creators won't get access to the project until it's paid (or ongoing)
        </p> : null}

        {/* Project Videos */}
        <Grid container direction="row" spacing={2}>
          <Grid item lg={3}>
            <label htmlFor="name">Creator</label>
          </Grid>
          <Grid item lg={2}>
            <label htmlFor="name">Video Type</label>
          </Grid>
          <Grid item lg={2}>
            Additional Attributes
          </Grid>
          <Grid item lg={1}>
            <label htmlFor="name">Payout</label>
          </Grid>
          <Grid item lg={2}>
            <label htmlFor="name">Price</label>
          </Grid>
          <Grid item lg={2}>
            <label htmlFor="name">Actions</label>
          </Grid>
        </Grid>
        <hr />

        {isVideosLoading ? "Loading.." : videos && videos.length > 0 ? videos.map((video, index) => {
          const fieldName = (name) => `videos.${index}.${name}`
          const isSponsored = video?.type_id === 2 // second video type is customized (static line)
          const isCustom = video?.type_id === 3 // third video type is customized (static line)

          return (<>
            <Grid container direction="row" spacing={2}>
              <Grid item lg={1}>
                {video.uid}
              </Grid>
              <Grid item lg={2}>
                {video.expert_id || editMode === video.id ? <Select
                  options={expertOptions}
                  isLoading={isExpertsLoading}
                  name={`${fieldName('expert_id')}`}
                  placeholder="Not assigned"
                  isDisabled={editMode !== video.id}
                  onChange={({ value }) => handleOnAssign(video.id, value)}
                  value={expertOptions.find(e => e.value === video.expert_id)}
                /> : 'Not Assigned'}
              </Grid>
              <Grid item lg={2}>
                <Select
                  options={typeOptions}
                  isLoading={isVideoTypesLoading}
                  name={`${fieldName('type_id')}`}
                  placeholder="Select video type"
                  isDisabled
                  value={typeOptions.find(e => e.value === video.type_id)}
                />
              </Grid>
              <Grid item lg={2}>
                {!(project.status_id === 2 || project.status_id === 4) || video.is_submitted !== 0 ?
                  <button
                    type="button"
                    className="stllr-btn primary btn-sm"
                    style={{ padding: "0px 8px" }}
                    onClick={() => setViewVideoDetailsOpen(video)}
                  >
                    View video data
                  </button>
                  :
                  <button
                    type="button"
                    className="stllr-btn primary btn-sm"
                    style={{ padding: "0px 8px" }}
                    disabled={!(project.status_id === 2 || project.status_id === 4) || video.is_submitted !== 0}
                    onClick={() => setVideoDetailsOpen(video)}
                  >
                    Fill video data
                  </button>
                }

                {/* <button
                    type="button"
                    className="stllr-btn primary btn-sm"
                    style={{ padding: "0px 8px" }}
                    disabled={!(project.status_id === 2 || project.status_id === 4) || video.is_submitted !== 0}
                    onClick={() => setVideoDetailsOpen(video)}
                  >
                    Fill video data
                  </button> */}
                {/* <Select
                  name={`${fieldName('content_type')}`}
                  options={contentTypes}
                  placeholder="Content type"
                  isDisabled={(project.status_id !== 4) && (project.status_id !== 2)}
                  value={contentTypes.find(e => e.value === video.content_type)}
                  onChange={({ value }) => setFieldValue(fieldName('content_type'), value)}
                  className='mb-10'
                />
                <Select
                  options={tones}
                  name={`${fieldName('tone')}`}
                  placeholder="Tone of voice"
                  isDisabled={(project.status_id !== 4) && (project.status_id !== 2)}
                  value={tones.find(e => e.value === video.tone)}
                  onChange={({ value }) => setFieldValue(fieldName('tone'), value)}
                  className='mb-10'
                />
                <Select
                  name={`${fieldName('language')}`}
                  options={languages}
                  placeholder="Language"
                  isDisabled={(project.status_id !== 4) && (project.status_id !== 2)}
                  value={languages.find(e => e.value === video.language)}
                  onChange={({ value }) => setFieldValue(fieldName('language'), value)}
                  className='mb-10'
                />
                <Select
                  name={`${fieldName('platform')}`}
                  options={platforms}
                  placeholder="Platform"
                  isDisabled={(project.status_id !== 4) && (project.status_id !== 2)}
                  value={platforms.find(e => e.value === video.platform)}
                  onChange={({ value }) => setFieldValue(fieldName('platform'), value)}
                  className='mb-10'
                />
                <Select
                  options={durations}
                  name={`${fieldName('duration')}`}
                  placeholder="Video duration"
                  isDisabled={(project.status_id !== 4) && (project.status_id !== 2)}
                  value={durations.find(e => e.value === video.duration)}
                  onChange={({ value }) => setFieldValue(fieldName('duration'), value)}
                  className='mb-10'
                />
                <Select
                  name={`${fieldName('video_format')}`}
                  options={formats}
                  placeholder="Video format"
                  isDisabled={(project.status_id !== 4) && (project.status_id !== 2)}
                  value={formats.find(e => e.value === video.video_format)}
                  onChange={({ value }) => setFieldValue(fieldName('video_format'), value)}
                  className='mb-10'
                />
                {isCustom ? (<div>
                  <Select
                    options={genders}
                    name={`${fieldName('gender')}`}
                    placeholder="Select gender"
                    isDisabled={(project.status_id !== 4) && (project.status_id !== 2)}
                    value={genders.find(e => e.value === video.gender)}
                    className='mb-10'
                  />
                  <Select
                    options={ages}
                    name={`${fieldName('age')}`}
                    placeholder="Select age"
                    isDisabled={(project.status_id !== 4) && (project.status_id !== 2)}
                    value={ages.find(e => JSON.stringify(e.value).replace(',', ', ') === video.age)}
                    className='mb-10'
                  />
                  <Select
                    options={places}
                    name={`${fieldName('place')}`}
                    placeholder="Select place"
                    isDisabled={(project.status_id !== 4) && (project.status_id !== 2)}
                    value={places.find(e => e.value === video.place)}
                  />
                </div>) : isSponsored ? (<div>
                  <Select
                    options={genders}
                    name={`${fieldName('gender')}`}
                    placeholder="Select gender"
                    isDisabled={(project.status_id !== 4) && (project.status_id !== 2)}
                    value={genders.find(e => e.value === video.gender)}
                  />
                </div>) : null} */}
              </Grid>
              <Grid item lg={1}>
                <div className="is-flex fdc aic">
                  {/* {calculatePayout(video, index, videos)} */}
                  {video.earning_formatted}
                  {video.reimburse ? <Tooltip title={'Reimburse amount'} arrow className='is-flex fdr aic mt-5'>
                      <ProductionQuantityLimitsOutlined className="ml-5" size={12} />
                      <span className="ml-5">{video.reimburse_formatted}</span>
                  </Tooltip> : ''}
                </div>
              </Grid>
              <Grid item lg={2}>
                {/* {calculatePrice(video)} */}
                {video.price_formatted}
              </Grid>
              <Grid item lg={2}>
                <div className="is-flex aic">
                  {video.leave_history?.length
                    ? <button
                      type="button"
                      className="stllr-btn primary btn-sm mr-10"
                      style={{ padding: "0px 8px" }}
                      onClick={() => setViewLeaveHistoryOpen(video.leave_history)}
                    >
                      Leave History
                    </button>
                    : null
                  }
                  {video.expert_id === null ? (
                    editMode === video.id
                      ? <button
                        type="button"
                        className="stllr-btn btn-sm"
                        style={{ padding: "0px 8px" }}
                        onClick={() => setEditMode(null)}
                      >
                        CANCEL
                      </button>
                      :
                      <div className="is-flex aic">
                        {video.is_canceled === 1 ?
                          <label>Video is cancelled</label>
                          :
                          <>
                            <button
                              type="button"
                              className="stllr-btn btn-sm primary mr-10"
                              style={{ padding: "0px 8px" }}
                              onClick={() => setEditMode(video.id)}
                              disabled={isAssigning || !video.is_submitted}
                            >
                              ASSIGN
                            </button>
                            <button
                              type="button"
                              className="stllr-btn primary btn-sm"
                              style={{ padding: "0px 8px" }}
                              disabled={!(project.status_id === 2 || project.status_id === 4) || video.is_submitted !== 1}
                              onClick={() => setVideoRecoOpen(video)}
                            >
                              Recommendations
                            </button>
                          </>
                        }
                      </div>
                  ) : (
                    <button
                      type="button"
                      className="stllr-btn btn-sm danger"
                      style={{ padding: "0px 8px" }}
                      onClick={() => handleOnUnassign(video.id, video)}
                      disabled={isUnassigning || video.is_approved}
                    >
                      UN-ASSIGN
                    </button>
                  )}
                  {video.is_approved && video.reimburse > 0 ? null : <button
                      type="button"
                      className="stllr-btn btn-sm primary ml-10"
                      style={{ padding: "0px 8px" }}
                      onClick={() => openReimburse(video)}
                    >
                      Reimburse
                    </button>
                  }
                </div>
              </Grid>
              <VideoDetails video={video} handleOnDeleteVideo={confirmDeleteVideoFile} handleOnDeleteScript={confirmDeleteScript} />
            </Grid>
            <hr />
          </>)
        }) : <div className="is-flex jcc">no videos added</div>}


        <div className="sep mb-18" />

        {/* <div className="is-flex aic mt-0 mb-10">
          <h4 className="mt-15 mr-15 dimmed">Brief Recommendations</h4>
        </div>

        {isLoading ? "Loading.." : <BriefRecommendations project={project} videos={videos} expertOptions={expertOptions} />} */}
        <VideoReimburseModal
          isOpen={Boolean(videoReimburseOpen)}
          onClose={closeReimburse}
          video={videoReimburseOpen}
          onSubmit={refetchVideos}
        />
        <VideoRecommendationsModal isOpen={videoRecoOpen} onClose={() => setVideoRecoOpen(null)} video={videoRecoOpen} expertOptions={expertOptions} />
        <FillVideoDetails
          isOpen={videoDetailsOpen}
          onClose={() => setVideoDetailsOpen(null)}
          onSubmit={handleOnVideoFormSubmit}
          video={videoDetailsOpen}
          contentTypes={contentTypes}
          tones={tones}
          languages={languages}
          platforms={platforms}
          durations={durations}
          formats={formats}
          genders={genders}
          ages={ages}
        />

        <ViewVideoDetails
          isOpen={viewVideoDetailsOpen}
          onClose={() => setViewVideoDetailsOpen(null)}
          video={viewVideoDetailsOpen}
          platforms={platforms}
          genders={genders}
        />

        <ViewLeaveHistoryModal
          isOpen={Boolean(viewLeaveHistoryOpen)}
          onClose={() => setViewLeaveHistoryOpen(null)}
          list={viewLeaveHistoryOpen}
        />
      </Container>
    </div>
  )
}

export default EditUGCProject

const VideoDetails = ({ video, handleOnDeleteVideo, handleOnDeleteScript }) => {

  console.log('video', video.file?.id)
  const [open, setOpen] = useState(false);
  const [originalUrl, setOriginalUrl] = useState(null);

  const { mutate: fetchOriginalUrl, isLoading: isFetchingUrl } = useMutation(fetchFileOriginalUrl, {
    onSuccess: (data) => {
      setOriginalUrl(data.url);
    },
    onError: (error) => {
      alert(error.data?.message || 'Failed to fetch original URL');
    }
  });

  return !video.expert_id ? null : (
    <React.Fragment>
      <Grid item xs={12}>
        <div className="is-flex aic jcsb" style={{ padding: "10px 0", borderBottom: "1px solid #e5e5e5" }}>
          <a
            className="color-blue"
            onClick={() => setOpen(v => !v)}
            style={{
              fontWeight: "600",
              fontSize: "14px",
              textDecoration: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center"
            }}
          >
            {open ? '▼ Hide details' : '▶ Show details'}
          </a>
        </div>
      </Grid>
      {open ? (
        <React.Fragment>
          <Grid item xs={12}>
            <div style={{
              background: "#f8f9fa",
              border: "1px solid #e5e5e5",
              borderRadius: "8px",
              padding: "20px",
              margin: "15px 0"
            }}>
              <h5 style={{
                margin: "0 0 15px 0",
                color: "#585858",
                fontSize: "16px",
                fontWeight: "600"
              }}>
                📄 Script Details
              </h5>
              {video.script ? (
                <div style={{ lineHeight: "1.6" }}>
                  <div style={{ marginBottom: "10px" }}>
                    <strong>Script #{video.script.number}:</strong>
                  </div>
                  <div style={{
                    background: "#fff",
                    padding: "12px",
                    borderRadius: "4px",
                    border: "1px solid #e8e8e8",
                    marginBottom: "10px"
                  }}>
                    {video.script.file ? (
                      <a
                        href={video.script.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#272AEE", textDecoration: "none" }}
                      >
                        📎 {video.script.file_url}
                      </a>
                    ) : (
                      <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{video.script.content}</p>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                    <div>
                      <strong>Requested Changes:</strong>
                      <span style={{
                        color: video.script.requested_changes ? "#db930c" : "#67b96b",
                        marginLeft: "5px"
                      }}>
                        {video.script.requested_changes || 'None'}
                      </span>
                    </div>
                    <div>
                      <strong>Status:</strong>
                      <span style={{
                        color: video.script.is_approved ? "#67b96b" : "#db930c",
                        marginLeft: "5px",
                        fontWeight: "600"
                      }}>
                        {video.script.is_approved ? '✅ Approved' : '⏳ Pending Approval'}
                      </span>
                    </div>
                  </div>
                    {!video.script.is_approved ? (
                      <div style={{ marginTop: "15px" }}>
                        <button
                          type="button"
                          className="stllr-btn btn-sm danger"
                          style={{ padding: "6px 12px" }}
                          onClick={() => handleOnDeleteScript(video.id, video.script.id)}
                        >
                          🗑️ Delete Video Script
                        </button>
                      </div>
                    ) : null}
                </div>
              ) : (
                <div style={{
                  color: "#a5a5a5",
                  fontStyle: "italic",
                  padding: "20px",
                  textAlign: "center",
                  background: "#fff",
                  borderRadius: "4px",
                  border: "1px dashed #e8e8e8"
                }}>
                  No script uploaded
                </div>
              )}
            </div>
          </Grid>
          <VideoScriptHistory scripts={video.script_history} />

          <Grid item xs={12}>
            <div style={{
              background: "#f8f9fa",
              border: "1px solid #e5e5e5",
              borderRadius: "8px",
              padding: "20px",
              margin: "15px 0"
            }}>
              <h5 style={{
                margin: "0 0 15px 0",
                color: "#585858",
                fontSize: "16px",
                fontWeight: "600"
              }}>
                🎥 Video Details
              </h5>
              {video.file ? (
                <div style={{ lineHeight: "1.6" }}>
                  <div style={{ marginBottom: "10px" }}>
                    <strong>Video #{video.file.number}:</strong>
                  </div>
                  <div style={{
                    background: "#fff",
                    padding: "12px",
                    borderRadius: "4px",
                    border: "1px solid #e8e8e8",
                    marginBottom: "10px"
                  }}>
                    <a
                      href={video.file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#272AEE", textDecoration: "none" }}
                    >
                      🎬 {video.file.url}
                    </a>
                  </div>
                  <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "15px" }}>
                    <div>
                      <strong>Requested Changes:</strong>
                      <span style={{
                        color: video.file.requested_changes ? "#db930c" : "#67b96b",
                        marginLeft: "5px"
                      }}>
                        {video.file.requested_changes || 'None'}
                      </span>
                    </div>
                    <div>
                      <strong>Status:</strong>
                      <span style={{
                        color: video.file.is_approved ? "#67b96b" : "#db930c",
                        marginLeft: "5px",
                        fontWeight: "600"
                      }}>
                        {video.file.is_approved ? '✅ Approved' : '⏳ Pending Approval'}
                      </span>
                    </div>
                  </div>

                  <div style={{ marginTop: "15px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="stllr-btn btn-sm primary"
                      style={{ padding: "6px 12px" }}
                      onClick={() => fetchOriginalUrl(video.file.id)}
                      disabled={isFetchingUrl}
                    >
                      {isFetchingUrl ? 'Loading...' : '🔗 View Original URL'}
                    </button>
                    {!video.file.is_approved ? (
                      <button
                        type="button"
                        className="stllr-btn btn-sm danger"
                        style={{ padding: "6px 12px" }}
                        onClick={() => handleOnDeleteVideo(video.id, video.file.id)}
                      >
                        🗑️ Delete Video File
                      </button>
                    ) : null}
                  </div>

                  {originalUrl && (
                    <div style={{
                      marginTop: "15px",
                      padding: "12px",
                      background: "#e8f4fd",
                      border: "1px solid #1e88e5",
                      borderRadius: "4px",
                      wordBreak: "break-all"
                    }}>
                      <strong>Original URL:</strong><br />
                      <a
                        href={originalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1e88e5", textDecoration: "none" }}
                      >
                        {originalUrl}
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  color: "#a5a5a5",
                  fontStyle: "italic",
                  padding: "20px",
                  textAlign: "center",
                  background: "#fff",
                  borderRadius: "4px",
                  border: "1px dashed #e8e8e8"
                }}>
                  No video uploaded
                </div>
              )}
            </div>
          </Grid>
          <VideoFileHistory files={video.file_history} />

          {video.is_approved ? (
            <Grid item xs={12}>
              <div style={{
                background: "#e8f5e8",
                border: "1px solid #67b96b",
                borderRadius: "8px",
                padding: "15px",
                margin: "15px 0"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "16px" }}>📋</span>
                  <strong style={{ color: "#48994c" }}>Review Status:</strong>
                  <span style={{
                    color: video.is_reviewed ? "#67b96b" : "#db930c",
                    fontWeight: "600"
                  }}>
                    {video.is_reviewed ? '✅ Reviewed' : '⏳ Pending Review'}
                  </span>
                </div>
              </div>
            </Grid>
          ) : null}
        </React.Fragment>
      ) : null}
    </React.Fragment>
  )
}

const VideoFileHistory = ({ files = [] }) => {
  const [open, setOpen] = useState(false);

  return files.length === 0 ? null : (
    <React.Fragment>
      <Grid item xs={12}>
        <div style={{ margin: "20px 0 10px 0" }}>
          <a
            className="color-blue"
            onClick={() => setOpen(v => !v)}
            style={{
              fontWeight: "600",
              fontSize: "14px",
              textDecoration: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            {open ? '▼ Hide video file history' : '▶ Show video file history'}
            <span style={{
              background: "#272AEE",
              color: "white",
              borderRadius: "12px",
              padding: "2px 8px",
              fontSize: "12px",
              fontWeight: "500"
            }}>
              {files.length}
            </span>
          </a>
        </div>
      </Grid>
      {open ? (
        <React.Fragment>
          <Grid item xs={12}>
            <div style={{
              background: "#f0f8ff",
              border: "1px solid #b3d9ff",
              borderRadius: "8px",
              padding: "15px",
              margin: "10px 0 20px 0"
            }}>
              <h6 style={{
                margin: "0 0 15px 0",
                color: "#1e40af",
                fontSize: "14px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                📚 Video File History ({files.length} files)
              </h6>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {files.map((file, index) => (
                  <div key={index} style={{
                    background: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    padding: "12px",
                    borderLeft: "4px solid #272AEE"
                  }}>
                    {file ? (
                      <div style={{ lineHeight: "1.5" }}>
                        <div style={{ marginBottom: "8px" }}>
                          <strong style={{ color: "#585858" }}>Video #{file.number}:</strong>
                        </div>
                        <div style={{
                          background: "#f8f9fa",
                          padding: "8px",
                          borderRadius: "4px",
                          marginBottom: "8px",
                          border: "1px solid #e8e8e8"
                        }}>
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#272AEE",
                              textDecoration: "none",
                              fontSize: "13px",
                              wordBreak: "break-all"
                            }}
                          >
                            🎬 {file.url}
                          </a>
                        </div>
                        <div style={{
                          display: "flex",
                          gap: "15px",
                          flexWrap: "wrap",
                          fontSize: "13px"
                        }}>
                          <div>
                            <strong>Changes:</strong>
                            <span style={{
                              color: file.requested_changes ? "#db930c" : "#67b96b",
                              marginLeft: "5px"
                            }}>
                              {file.requested_changes || 'None'}
                            </span>
                          </div>
                          <div>
                            <strong>Status:</strong>
                            <span style={{
                              color: file.is_approved ? "#67b96b" : "#db930c",
                              marginLeft: "5px",
                              fontWeight: "600"
                            }}>
                              {file.is_approved ? '✅ Approved' : '⏳ Pending'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        color: "#a5a5a5",
                        fontStyle: "italic",
                        textAlign: "center",
                        padding: "10px"
                      }}>
                        No video data available
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Grid>
        </React.Fragment>
      ) : null}
    </React.Fragment>
  )
}

const VideoScriptHistory = ({ scripts = [] }) => {
  const [open, setOpen] = useState(false);

  return scripts.length === 0 ? null : (
    <React.Fragment>
      <Grid item xs={12}>
        <div style={{ margin: "20px 0 10px 0" }}>
          <a
            className="color-blue"
            onClick={() => setOpen(v => !v)}
            style={{
              fontWeight: "600",
              fontSize: "14px",
              textDecoration: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            {open ? '▼ Hide video script history' : '▶ Show video script history'}
            <span style={{
              background: "#272AEE",
              color: "white",
              borderRadius: "12px",
              padding: "2px 8px",
              fontSize: "12px",
              fontWeight: "500"
            }}>
              {scripts.length}
            </span>
          </a>
        </div>
      </Grid>
      {open ? (
        <React.Fragment>
          <Grid item xs={12}>
            <div style={{
              background: "#fff5f0",
              border: "1px solid #ffd6b3",
              borderRadius: "8px",
              padding: "15px",
              margin: "10px 0 20px 0"
            }}>
              <h6 style={{
                margin: "0 0 15px 0",
                color: "#c2410c",
                fontSize: "14px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                📝 Script History ({scripts.length} scripts)
              </h6>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {scripts.map((script, index) => (
                  <div key={index} style={{
                    background: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    padding: "12px",
                    borderLeft: "4px solid #f59e0b"
                  }}>
                    {script ? (
                      <div style={{ lineHeight: "1.5" }}>
                        <div style={{ marginBottom: "8px" }}>
                          <strong style={{ color: "#585858" }}>Script #{script.number}:</strong>
                        </div>
                        <div style={{
                          background: "#f8f9fa",
                          padding: "8px",
                          borderRadius: "4px",
                          marginBottom: "8px",
                          border: "1px solid #e8e8e8"
                        }}>
                          {script.file ? (
                            <a
                              href={script.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#272AEE",
                                textDecoration: "none",
                                fontSize: "13px",
                                wordBreak: "break-all"
                              }}
                            >
                              📎 {script.file_url}
                            </a>
                          ) : (
                            <p style={{
                              margin: 0,
                              whiteSpace: "pre-wrap",
                              fontSize: "13px",
                              color: "#585858",
                              maxHeight: "100px",
                              overflow: "auto"
                            }}>
                              {script.content}
                            </p>
                          )}
                        </div>
                        <div style={{
                          display: "flex",
                          gap: "15px",
                          flexWrap: "wrap",
                          fontSize: "13px"
                        }}>
                          <div>
                            <strong>Changes:</strong>
                            <span style={{
                              color: script.requested_changes ? "#db930c" : "#67b96b",
                              marginLeft: "5px"
                            }}>
                              {script.requested_changes || 'None'}
                            </span>
                          </div>
                          <div>
                            <strong>Status:</strong>
                            <span style={{
                              color: script.is_approved ? "#67b96b" : script.is_deleted ? "#bb4c4c" : "#db930c",
                              marginLeft: "5px",
                              fontWeight: "600"
                            }}>
                              {script.is_approved ? '✅ Approved' : script.is_deleted ? '🗑️ Deleted' : '⏳ Pending'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        color: "#a5a5a5",
                        fontStyle: "italic",
                        textAlign: "center",
                        padding: "10px"
                      }}>
                        No script data available
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Grid>
        </React.Fragment>
      ) : null}
    </React.Fragment>
  )
}